import {useTheme} from "@mui/material/styles";
import React, {useEffect, useMemo, useState} from "react";
import ViewEditDialog from "../../utils/ui-components/ViewEditDialog";
import {
    Box,
    Button,
    Fab,
    Grid, IconButton,
    InputAdornment,
    Paper,
    Table, TableBody, TableCell,
    TableContainer,
    TableHead, TablePagination,
    TableRow, Tooltip, Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MainCard from "../../utils/ui-components/MainCard";
import {gridSpacing} from "../../store/constants";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EmptyResult from "../../utils/ui-components/EmptyResult";
import Spinner from "../../utils/ui-components/Spinner";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";
import ReportUserDetails from "./report-user-details";
import { UserService } from "../../assets/_services/user-service";
import { Form, Formik } from "formik";
import * as Yup from 'yup';
import TextFieldWrapper from "../../utils/ui-components/FormsUI/TextField";
import { formatMobile } from "../../utils/utils";
import { MENU_OPEN } from "../../redux/actions/actions";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

export interface ReportUserListValues {
    reportUserId: number | string;
    username: string;
    password: string;
    confirmPassword: string;
    status: boolean;
    email: string;
    mobileNumber : string | number
};

function ReportUserList() {

    const INITIAL_FORM_STATE: ReportUserListValues = {
        reportUserId : "",
        confirmPassword: "",
        username: "",
        password: "",
        status: true,
        email: "",
        mobileNumber: ""
    };

    const theme: any = useTheme();
    const [search, setSearch] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [reportUsers, setReportUsers] = useState([]);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [count, setCount] = useState(0);

    const [open, setOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [initialItem, setInitialItem] = useState(INITIAL_FORM_STATE);
    
    const path = useLocation();
    const dispatch = useDispatch();

    useEffect(()=>{
        const id = path?.pathname
        dispatch({type: MENU_OPEN,id})
    },[])

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchReportUserList(search);
    }, [page, rowsPerPage]);

    const fetchReportUserList = (value : string) => {
        setIsLoading(true);
        let formData : any = {}
        if(value){
            formData = {
                userName : value
            }
        }
        UserService.getAllReportUser(formData, page, rowsPerPage).then(
            response => {
                if (response.isSuccess) {
                    setReportUsers(response.data.data.rows)
                    setCount(response.data.data.count)
                    setIsLoading(false);
                }else{
                    setIsLoading(false);
                }
            }
        );
    }

    const handleClickOpen = (dialogTitle: string, formState: ReportUserListValues) => {
        setOpen(true);
        setDialogTitle(dialogTitle);
        setInitialItem(formState);
    };

    const dialog = useMemo(() => ViewEditDialog(ReportUserDetails)({
        open: open,
        setOpen: setOpen,
        dialogTitle: dialogTitle,
        initialItem: initialItem,
        fetchData: fetchReportUserList,
        theme: theme,
        maxWidth: "md",
        initialData : {search: search, setSearch:setSearch}
    }), [open]);

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Grid container>
            <Grid item xs={12}>
                <Fab
                    color="primary"
                    sx={{position: "fixed", zIndex: 3, bottom: 16, right: 16}}
                    aria-label="add"
                    onClick={() => handleClickOpen(`Add Report User`, INITIAL_FORM_STATE)}
                >
                    <AddIcon/>
                </Fab>
            </Grid>
            <Grid container justifyContent="center">
                <Grid item xl={9} lg={10} md={10} sm={12} xs={12}>
                    <MainCard title="Report User List">
                    <Formik
                        initialValues={{
                            userName : ""
                        }}
                        validationSchema={
                            Yup.object().shape({
                                userName: Yup.string().notRequired(),
                            })
                        }
                        onSubmit={(values: any, {setSubmitting}) => {
                            setPage(0)
                            setSearch(values.userName || "")
                            fetchReportUserList(values.userName)
                        }}
                        >
                        {({values, dirty, isSubmitting, isValid, setFieldValue}) => (
                            <Form>
                                <Grid container spacing={gridSpacing}>
                                    <Grid item lg={6} md={6} sm={5} xs={8}>
                                        <TextFieldWrapper
                                            label="Search"
                                            placeholder="Search Admin Users.."
                                            name='userName'
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon/>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{...theme.typography.customInput}}
                                            fullWidth={true}
                                        />
                                    </Grid>
                                    <Grid item lg={3} md={3} sm={6} xs={4}>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            sx={{...theme.typography.customInput}}
                                            type="submit"
                                        >
                                            View
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                        </Formik>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell >ID</TableCell>
                                        <TableCell >Username</TableCell>
                                        <TableCell >Email</TableCell>
                                        <TableCell sx={{maxWidth:'160px'}}>Mobile Number</TableCell>
                                        <TableCell align="center" >Status</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportUsers?.length > 0 && !isLoading && reportUsers.map((item: any) => (
                                        <TableRow hover key={item?.id}>
                                            <TableCell>{item?.id}</TableCell>
                                            <TableCell>{item?.userName}</TableCell>
                                            <TableCell>{item?.email}</TableCell>
                                            <TableCell sx={{maxWidth:'160px'}}>{formatMobile(item?.mobile)}</TableCell>
                                            <TableCell align="center">{item?.status ?
                                                <Typography variant="overline"
                                                            color='success.main'>Active</Typography> :
                                                <Typography variant="overline"
                                                            color='error.main'>Inactive</Typography>}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box>
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            aria-label="view"
                                                            color ='primary'
                                                            onClick={() => {
                                                                let INITIAL_FORM_STATE = {
                                                                    reportUserId: item?.id,
                                                                    username: item?.userName,
                                                                    password: "",
                                                                    status: item?.status ? true : false,
                                                                    confirmPassword : "",
                                                                    email : item?.email,
                                                                    mobileNumber : item?.mobile,
                                                                };
                                                                // getAllMask();
                                                                handleClickOpen(
                                                                    `Edit Report User ID: ${item.id}`,
                                                                    INITIAL_FORM_STATE,
                                                                );
                                                            }}
                                                        >
                                                            <VisibilityIcon
                                                                fontSize="inherit"
                                                                color="primary"
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {!reportUsers?.length && !isLoading ? <EmptyResult/> : isLoading ? <Box
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexDirection: "column",
                                    minHeight: "40vh",
                                }}
                            >
                                <Spinner/>
                            </Box> : null}
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 100]}
                                component="div"
                                count={count}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                            />
                        </TableContainer>
                    </MainCard>
                </Grid>
            </Grid>
            {dialog}
        </Grid>
    );
}

export default ReportUserList;